import { Typography } from "@mui/material";
import parsePhoneNumber from "libphonenumber-js";
import { useTranslation } from "react-i18next";
import { Box, CopyButton, FlexColBox } from "../../common/components";
import { createStyles, palette, spacing } from "../../styles/theme";
import { AddressType, AddressTypeEnum } from "../../types";
import { formatEmailForDisplay } from "../../utils/userAddress.utils";

const styles = createStyles({
  row: {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    "& #copy-button": { display: "none" },
    "&:hover": {
      "& #copy-button": { display: "flex" },
    },
  },
  text: {
    lineHeight: "20px",
  },
});

interface Props {
  address: AddressType;
  addressType: AddressTypeEnum;
  title?: string;
  fromAdmin?: boolean;
  hideCopyButton?: boolean;
}

const AddressBox = ({
  address,
  title,
  addressType,
  fromAdmin,
  hideCopyButton,
}: Props) => {
  const { t } = useTranslation();
  let formattedPhone = address.phone;
  if (address.phone) {
    const parsedPhoneNumber = parsePhoneNumber(
      address.phone,
      address?.address?.country || "CA"
    );

    if (parsedPhoneNumber) {
      formattedPhone = parsedPhoneNumber.formatNational();
    }
  }

  const fieldColor = fromAdmin ? palette.grey.primary : palette.grey.nearBlack;

  return (
    <FlexColBox gap={{ xs: spacing.smallMobile, lg: spacing.smallDesktop }}>
      {title && (
        <Typography
          variant="textSm"
          color={fromAdmin ? palette.grey.nearBlack : palette.grey.primary}
        >
          {title}
        </Typography>
      )}
      {address && (
        <FlexColBox gap={{ xs: spacing.xsMobile, lg: spacing.xsDesktop }}>
          {address.id === "default" ? (
            <Typography variant="textRegular" color={fieldColor}>
              {t("checkout.use_default_billing_address")}
            </Typography>
          ) : (
            <>
              {[
                address.organization,
                `${address.firstName} ${address.lastName}`,
                address.communicationLanguage &&
                addressType === AddressTypeEnum.SHIPPING
                  ? t(`general.language.${address.communicationLanguage}`)
                  : "",
                address.address.line1,
                address.address.line2,
                address.address.city,
                address.address.state,
                address.address.zipcode,
              ]
                .filter((x) => !!x && !x.includes("undefined"))
                .map((x, index) => (
                  <Box sx={styles.row} key={`${x}-${index}`}>
                    <Typography
                      key={`${x}-${index}`}
                      variant="textRegular"
                      color={fieldColor}
                      sx={styles.text}
                    >
                      {x}
                    </Typography>
                    {!hideCopyButton && <CopyButton value={x} size="medium" />}
                  </Box>
                ))}
              <Box sx={styles.row}>
                <Typography
                  variant="textRegular"
                  color={fieldColor}
                  sx={styles.text}
                >
                  {t(`general.country.${address.address.country}`)}
                </Typography>
                {!hideCopyButton && (
                  <CopyButton
                    value={t(`general.country.${address.address.country}`)}
                    size="medium"
                  />
                )}
              </Box>
              {[formattedPhone, formatEmailForDisplay(address.email)]
                .filter((x) => !!x)
                .map((x, index) => (
                  <Box sx={styles.row} key={`${x}-${index}`}>
                    <Typography
                      key={`${x}-${index}`}
                      variant="textRegular"
                      color={fieldColor}
                      sx={styles.text}
                    >
                      {x}
                    </Typography>
                    {!hideCopyButton && <CopyButton value={x} size="medium" />}
                  </Box>
                ))}
            </>
          )}
        </FlexColBox>
      )}
    </FlexColBox>
  );
};

export default AddressBox;
